import { ChevronRight, ClipboardList } from 'lucide-react'
import { FC } from 'react'

interface SurveyActionBarProps {
  total: number
  completed: boolean
  available: boolean
  onOpen: () => void
  showWaitingMessage?: boolean
}

const SurveyActionBar: FC<SurveyActionBarProps> = ({
  total,
  completed,
  available,
  onOpen,
  showWaitingMessage = false,
}) => {
  if (available && !completed) {
    return (
      <>
        <SurveyActionBarStyles />
        <button
          type="button"
          onClick={onOpen}
          className="survey-action-bar flex h-12 w-full cursor-pointer items-center gap-2 rounded-[10px] border px-3 py-2 text-left transition-colors"
        >
          <SurveyIcon />
          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-center text-[15px] font-bold text-slate-900">
            <span className="survey-action-label-full">匿名アンケートにご協力ください（全{total}問）</span>
            <span className="survey-action-label-short">匿名アンケート（全{total}問）</span>
          </span>
          <span className="survey-action-answer survey-action-cta shrink-0 rounded-md px-2.5 py-[5px] text-[14px] font-bold">
            回答する
          </span>
          {/* 狭い幅では「回答する」の代わりに矢印を置き、見出しを最後まで出す */}
          <ChevronRight className="survey-action-chevron h-5 w-5 shrink-0" aria-hidden="true" />
        </button>
      </>
    )
  }

  if (completed) {
    return (
      <>
        <SurveyActionBarStyles />
        <div className="survey-action-bar flex h-12 w-full items-center justify-center rounded-[10px] border px-3 py-2 text-center text-[15px] font-medium text-slate-500">
          アンケートに回答済みです
        </div>
      </>
    )
  }

  return (
    <>
      <SurveyActionBarStyles />
      <div className="survey-action-bar flex h-12 w-full items-center gap-2 rounded-[10px] border px-3 py-2">
        <SurveyIcon />
        <span className="min-w-0 flex-1">
          <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-center text-[15px] font-bold text-slate-900">
            <span className="survey-action-label-full">匿名アンケートにご協力ください（全{total}問）</span>
            <span className="survey-action-label-short">匿名アンケート（全{total}問）</span>
          </span>
          {showWaitingMessage && <span className="block text-center text-[11px] text-slate-600">再生が進むと表示されます</span>}
        </span>
      </div>
    </>
  )
}

const SurveyIcon = () => (
  <span className="survey-action-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
    <ClipboardList className="h-4 w-4" />
  </span>
)

const SurveyActionBarStyles = () => (
  <style>{`
    .survey-action-bar {
      container-type: inline-size;
      border-color: color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 24%, transparent);
      background: color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 8%, white);
    }

    button.survey-action-bar:hover {
      background: color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 14%, white);
    }

    .survey-action-icon,
    .survey-action-answer {
      background: color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 14%, white);
      color: var(--video-brand-color, var(--video-accent-color));
    }

    .survey-action-chevron {
      color: var(--video-brand-color, var(--video-accent-color));
    }

    .survey-action-label-short,
    .survey-action-chevron {
      display: none;
    }

    @container (max-width: 430px) {
      .survey-action-label-full,
      .survey-action-cta {
        display: none;
      }

      .survey-action-label-short {
        display: inline;
      }

      .survey-action-chevron {
        display: block;
      }
    }
  `}</style>
)

export default SurveyActionBar
